import { Button } from "@/lib/components/Button/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import { Input } from "@/lib/components/Input";
import Toast from "@/lib/components/Toast";
import { AnimatedImagePresetDto } from "@repo/api/animated-image-preset";
import { SourceImagePresetDto } from "@repo/api/source-image-preset";
import { useState } from "react";
import { useUpdateAnimatedImagePresetMutation } from "@/lib/queries/animated-image-preset-queries";
import { useUpdateSourceImagePresetMutation } from "@/lib/queries/source-image-preset-queries";

type UpdateAnimationPresetDialogProps = {
  source: "ANIMATION";
  selectedPreset: AnimatedImagePresetDto;
};

type UpdateSourcePresetDialogProps = {
  source: "EDIT_IMAGE";
  selectedPreset: SourceImagePresetDto;
};

type UpdatePromptPresetDialogProps = {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  teamExternalId: string;
} & (UpdateAnimationPresetDialogProps | UpdateSourcePresetDialogProps);

export function UpdatePromptPresetDialog({
  open,
  onOpenChange,
  source,
  selectedPreset,
  teamExternalId,
}: UpdatePromptPresetDialogProps) {
  const [name, setName] = useState(selectedPreset.name);

  const updateAnimatedPreset = useUpdateAnimatedImagePresetMutation({
    teamExternalId,
    onSuccess: () => {
      Toast.info("Prompt updated successfully");
      onOpenChange(false);
    },
  });

  const updateSourcePreset = useUpdateSourceImagePresetMutation({
    teamExternalId,
    onSuccess: () => {
      Toast.info("Prompt updated successfully");
      onOpenChange(false);
    },
  });

  const isSaving =
    updateAnimatedPreset.isPending || updateSourcePreset.isPending;

  const handleSave = () => {
    if (isSaving) return;

    if (source === "ANIMATION") {
      updateAnimatedPreset.mutate({
        presetExternalId: selectedPreset.externalId,
        data: {
          name,
          provider: selectedPreset.provider,
          customInput: selectedPreset.customInput,
          tags: selectedPreset.tags,
        },
      });
    } else {
      updateSourcePreset.mutate({
        presetExternalId: selectedPreset.externalId,
        data: {
          name,
          provider: selectedPreset.provider,
          customInput: selectedPreset.customInput,
          tags: selectedPreset.tags,
        },
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="w-full">
          <DialogTitle>Update Prompt</DialogTitle>
          <DialogDescription>
            Update the name for &ldquo;
            {selectedPreset.name || "Untitled prompt"}&rdquo;.
          </DialogDescription>
        </DialogHeader>
        <div className="flex w-full flex-col gap-4">
          <div className="w-full">
            <label className="input-label block">Prompt Name</label>
            <Input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value.replace(/\s{2,}/g, " ").slice(0, 20));
              }}
              placeholder="Enter prompt name"
              maxLength={20}
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={handleSave} disabled={isSaving || !name.trim()}>
            Update Prompt
          </Button>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isSaving}>
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
